import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { KpiCard, StatusChip } from "@/components/kpi/KpiCard";
import { AlertList } from "@/components/kpi/AlertList";
import { MATRIX_CODES, STATUS_ORDER, VIEWS, defaultView, formatKpi, statusOf } from "@/lib/kpi";

const TABS = [...Object.entries(VIEWS).map(([id, v]) => ({ id, label: v.label })), { id: "alerts", label: "Alerts" }];

function Tile({ status, count }) {
  const s = statusOf(status);
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900" data-testid={`tile-${status}`}>
      <span className={`h-9 w-1.5 rounded-full ${s.bar}`} aria-hidden="true" />
      <div>
        <div className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-50">{count}</div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground"><s.icon className="h-3 w-3" aria-hidden="true" />{s.label}</div>
      </div>
    </div>
  );
}

/** Management view: one row per project, one column per headline KPI; each cell shows status (icon + value) and links to the source. */
function ProjectMatrix({ rows, names }) {
  const projects = useMemo(() => {
    const by = {};
    rows.filter((r) => r.project_id).forEach((r) => { (by[r.project_id] ||= { id: r.project_id, code: r.project_code, name: r.project_name, cells: {} }).cells[r.code] = r; });
    return Object.values(by).sort((a, b) => worst(a) - worst(b) || (a.code || "").localeCompare(b.code || ""));
  }, [rows]);
  const cols = MATRIX_CODES.filter((c) => rows.some((r) => r.code === c));
  if (!projects.length) return null;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <table className="w-full text-sm" data-testid="kpi-matrix">
        <thead className="bg-[#ECF4F7] text-left text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <tr><th className="px-4 py-2.5 font-semibold">Project</th>{cols.map((c) => <th key={c} className="px-3 py-2.5 font-semibold">{names[c] || c}</th>)}</tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.id} className="border-t border-slate-100 dark:border-slate-800">
              <td className="px-4 py-2.5">
                <div className="font-medium text-slate-800 dark:text-slate-100">{p.code}</div>
                <div className="max-w-[16rem] truncate text-xs text-muted-foreground">{p.name}</div>
              </td>
              {cols.map((c) => {
                const r = p.cells[c];
                if (!r) return <td key={c} className="px-3 py-2.5 text-muted-foreground">—</td>;
                const s = statusOf(r.status);
                return (
                  <td key={c} className="px-3 py-2.5">
                    <Link to={r.link || "#"} className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 hover:bg-slate-50 dark:hover:bg-slate-800" title={`${r.name}: ${s.label}`}>
                      <s.icon className={`h-3.5 w-3.5 ${r.status === "RED" ? "text-red-600" : r.status === "AMBER" ? "text-amber-600" : r.status === "GREEN" ? "text-emerald-600" : "text-slate-400"}`} aria-hidden="true" />
                      <span className="tabular-nums text-slate-800 dark:text-slate-100">{formatKpi(r.value, r.unit)}</span>
                      <span className="sr-only">{s.label}</span>
                    </Link>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
const worst = (p) => Math.min(...Object.values(p.cells).map((r) => STATUS_ORDER[r.status] ?? 3));

/** KPI dashboard: green / amber / red cards per role, trend against the last run, sparklines of recent runs, drill-down links,
 *  and the alert list with acknowledge-with-note. What each person sees is filtered by the server (their projects and modules). */
export default function KpiDashboard() {
  const { user, isOrgAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || defaultView(user?.role);
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertFilter, setAlertFilter] = useState("OPEN");
  const [projectId, setProjectId] = useState(params.get("project") || "");
  const [running, setRunning] = useState(false);
  const [failed, setFailed] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: k }, { data: a }] = await Promise.all([
        api.get("/kpis", { params: { history: 12 } }),
        api.get("/kpis/alerts", { params: alertFilter === "ALL" ? {} : { status: alertFilter } }),
      ]);
      setData(k); setAlerts(a); setFailed(null);
    } catch (err) { setFailed(getErrorMessage(err)); }
  }, [alertFilter]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => data?.kpis || [], [data]);
  const names = useMemo(() => Object.fromEntries(rows.map((r) => [r.code, r.name])), [rows]);
  const projects = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.project_id) m.set(r.project_id, `${r.project_code}${r.project_name ? ` · ${r.project_name}` : ""}`); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const chosenProject = projectId && projects.some(([id]) => id === projectId) ? projectId : projects[0]?.[0] || "";
  const counts = useMemo(() => rows.reduce((c, r) => ({ ...c, [r.status]: (c[r.status] || 0) + 1 }), {}), [rows]);

  const shown = useMemo(() => {
    const v = VIEWS[view];
    if (!v) return [];
    let list = v.codes ? rows.filter((r) => v.codes.includes(r.code)) : rows;
    if (view === "project") list = list.filter((r) => r.project_id === chosenProject);
    const order = v.codes ? Object.fromEntries(v.codes.map((c, i) => [c, i])) : {};
    return [...list].sort((a, b) => (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || ((order[a.code] ?? 0) - (order[b.code] ?? 0)) || (a.project_code || "").localeCompare(b.project_code || ""));
  }, [rows, view, chosenProject]);

  const setView = (id) => setParams((p) => { const n = new URLSearchParams(p); n.set("view", id); return n; });
  const runNow = async () => {
    setRunning(true);
    try {
      const { data: c } = await api.post("/kpis/run");
      toast.success(`Updated: ${c.RED} red, ${c.AMBER} amber, ${c.GREEN} green${c.errors ? `, ${c.errors} could not be worked out` : ""}`);
      await load();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setRunning(false); }
  };

  return (
    <div className="space-y-5" data-testid="kpi-dashboard">
      <PageHeader title="Performance"
        description={data?.computed_at ? `Key indicators and alerts · last worked out ${new Date(data.computed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : "Key indicators and alerts for your projects."}
        action={isOrgAdmin && (
          <div className="flex gap-2">
            <Button asChild variant="ghost" size="sm"><Link to="/erp/governance/kpi-settings" data-testid="kpi-thresholds-link">Thresholds</Link></Button>
            <Button variant="outline" size="sm" onClick={runNow} disabled={running} data-testid="kpi-run-now">
              {running ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />} Work out now
            </Button>
          </div>
        )} />

      <div className="flex flex-wrap gap-1 rounded-xl bg-[#ECF4F7] p-1 dark:bg-slate-800" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={view === t.id} onClick={() => setView(t.id)} data-testid={`kpi-tab-${t.id}`}
            className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition ${view === t.id ? "bg-white text-[#910029] shadow-sm dark:bg-slate-900 dark:text-rose-300" : "text-slate-600 hover:text-slate-900 dark:text-slate-300"}`}>
            {t.label}{t.id === "alerts" && alerts.length > 0 && alertFilter === "OPEN" ? ` (${alerts.length})` : ""}
          </button>
        ))}
      </div>

      {failed && <p className="text-sm text-destructive" role="alert">{failed} <Button variant="link" onClick={load}>Try again</Button></p>}
      {!data && !failed && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>}

      {data && view !== "alerts" && (
        <>
          {!rows.length ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center dark:border-slate-700 dark:bg-slate-900" data-testid="kpi-empty">
              <p className="font-medium text-slate-800 dark:text-slate-100">No indicators yet</p>
              <p className="mt-1 text-sm text-muted-foreground">{isOrgAdmin ? "Click “Work out now” to calculate them for every project." : "They appear after the next run, for the projects and modules you can open."}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{VIEWS[view]?.hint}</p>
              {view === "management" && (
                <>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {["RED", "AMBER", "GREEN", "NO_DATA"].map((s) => <Tile key={s} status={s} count={counts[s] || 0} />)}
                  </div>
                  <ProjectMatrix rows={rows} names={names} />
                  <h2 className="pt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">Needs attention</h2>
                </>
              )}
              {view === "project" && projects.length > 0 && (
                <Select value={chosenProject} onValueChange={(v) => { setProjectId(v); setParams((p) => { const n = new URLSearchParams(p); n.set("project", v); return n; }); }}>
                  <SelectTrigger className="w-80" aria-label="Project"><SelectValue placeholder="Choose a project" /></SelectTrigger>
                  <SelectContent>{projects.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {(() => {
                const cards = view === "management" ? shown.filter((r) => r.status === "RED" || r.status === "AMBER" || !r.project_id) : shown;
                return cards.length ? (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="kpi-cards">
                    {cards.map((r) => <KpiCard key={`${r.code}-${r.project_id || r.company_id}`} row={r} showScope={view !== "project"} />)}
                  </div>
                ) : <p className="text-sm text-muted-foreground">{view === "management" ? "Nothing is amber or red." : "No indicators here for what you can open."}</p>;
              })()}
            </>
          )}
        </>
      )}

      {view === "alerts" && (
        <div className="rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
            <div className="text-sm font-semibold">Alerts</div>
            <div className="flex gap-1">
              {["OPEN", "ACKNOWLEDGED", "RESOLVED", "ALL"].map((f) => (
                <Button key={f} size="sm" variant={alertFilter === f ? "default" : "ghost"} onClick={() => setAlertFilter(f)} data-testid={`alert-filter-${f}`}>
                  {f === "ALL" ? "All" : f.charAt(0) + f.slice(1).toLowerCase()}
                </Button>
              ))}
            </div>
          </div>
          <AlertList alerts={alerts} onChanged={load} emptyText={alertFilter === "OPEN" ? "No open alerts. Everything is within its limits." : "Nothing here."} />
        </div>
      )}

      {data && view !== "alerts" && rows.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <StatusChip status="RED" /> act today · <StatusChip status="AMBER" /> watch · <StatusChip status="GREEN" /> on track. Click a card to open the documents behind it.
        </p>
      )}
    </div>
  );
}
